/**
 * Display-side money. Formatting only.
 *
 * The server sends integer paise and every amount stays an integer all the way
 * to the DOM — the browser never adds, subtracts or re-derives a figure. If a
 * screen needs a total, the API sends the total. A number computed in a browser
 * and shown next to one computed on the server is how two people end up
 * quoting different figures for the same cycle.
 *
 * Mirrors lereco-backend/src/lib/money.js formatRupees(); kept in sync by hand,
 * which is fine for one function and one direction.
 */

/**
 * @param {number} paise
 * @param {{symbol?: boolean, showPaise?: boolean}} [opts]
 *   `showPaise: false` drops a trailing `.00` — for summary tiles, where the
 *   two zeros are noise. Never use it in a table an accountant reconciles
 *   against: there, a hidden 47 paise is a difference nobody can see.
 */
export function formatRupees(paise, opts = {}) {
  if (paise === null || paise === undefined) return '—'
  if (!Number.isInteger(paise)) return 'ERR'

  const { symbol = true, showPaise = true } = opts
  if (!showPaise && paise % 100 === 0) {
    return formatRupees(paise, { symbol }).replace(/\.00$/, '')
  }
  const negative = paise < 0
  const absolute = Math.abs(paise)
  const whole = Math.floor(absolute / 100)
  const frac = String(absolute % 100).padStart(2, '0')

  // Indian grouping — 1,23,456.78. Every reader of this screen reads Indian
  // ledgers; Intl's en-IN does the same but this stays dependency-free and
  // identical to the server's implementation.
  const digits = String(whole)
  let grouped
  if (digits.length <= 3) {
    grouped = digits
  } else {
    const last3 = digits.slice(-3)
    const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
    grouped = `${rest},${last3}`
  }

  return `${negative ? '-' : ''}${symbol ? '₹' : ''}${grouped}.${frac}`
}

